import {
  Component, HostListener, OnDestroy, OnInit,
  signal
} from '@angular/core';
import { FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';

import { filter, Subject, switchMap, takeUntil } from 'rxjs';

import { ProfilePictureSize } from '@sondermind/constants';

import { PartialProviderWithId, ProviderUserService } from '@sondermind/data-access/provider-user';
import { SondermindApplicationService } from '@sondermind/data-access/sondermind-app-service';
import { UserProfileHttpService } from '@sondermind/data-access/therapist-profile';
import { IrisButtonTypes } from '@sondermind/iris/button';
import { DropdownOption } from '@sondermind/iris/dropdown';
import { IrisModalService, IrisModalSize } from '@sondermind/iris/modal';
import { IComponentCanDeactivate } from '@sondermind/features/provider-facing/provider-profile';
import {
  ProfilePhotoUploaderComponent,
  IProfilePhotoUploaderInput,
  IProfilePhotoUploaderResult
} from '@sondermind/features/profile-photo-uploader';

import { IAPIFetchRequestOptions } from '@sondermind/utilities/models-http';
import { ProviderUser } from '@sondermind/utilities/models-user-provider';
import { FieldMaskingService } from '@sondermind/utilities/services/field-masking.service';
import { PhoneValidator } from '@sondermind/utilities/tools/validators/phone.validator';

import { DISALLOWED_TERMS, PROFANITY } from './disallowed-terms';

enum PronounOption {
  NO_PRONOUNS_SHARED = 'No pronouns shared',
  SHE_HER = 'she/her',
  SHE_THEY = 'she/they',
  HE_HIM = 'he/him',
  HE_THEY = 'he/they',
  THEY_THEM = 'they/them',
  ZE_ZIR = 'ze/zir',
  ZE_HIR = 'ze/hir',
  ASK = 'Ask me about my pronouns'
}

enum ProfilePicView {
  WEB = 'web',
  MOBILE = 'mob'
}

interface IAboutYouForm {
  id: FormControl<number | null>;
  preferredFirstName: FormControl<string | null>;
  publicPhoneNumber: FormControl<string | null>;
  pronouns: FormControl<string | null>;
}

@Component({
  selector: 'member-page-about-you',
  templateUrl: './profile-about-you.component.html',
  styleUrls: [
    './profile-about-you.component.scss',
    '../../styles/profile-app-shared-styles.scss'
  ],
  standalone: false
})
export class ProfileAboutYouPage implements OnInit, OnDestroy, IComponentCanDeactivate {
  user!: ProviderUser;
  userInitial!: string;
  form!: FormGroup<IAboutYouForm>;
  saving: boolean = false;
  destroyed$ = new Subject<void>();

  irisButtonTypes = IrisButtonTypes;
  profilePicView = ProfilePicView;
  profilePictureSize = ProfilePictureSize;
  pronounList: Array<DropdownOption<string>> = [
    { name: PronounOption.NO_PRONOUNS_SHARED, value: PronounOption.NO_PRONOUNS_SHARED },
    { name: PronounOption.SHE_HER, value: PronounOption.SHE_HER },
    { name: PronounOption.SHE_THEY, value: PronounOption.SHE_THEY },
    { name: PronounOption.HE_HIM, value: PronounOption.HE_HIM },
    { name: PronounOption.HE_THEY, value: PronounOption.HE_THEY },
    { name: PronounOption.THEY_THEM, value: PronounOption.THEY_THEM },
    { name: PronounOption.ZE_ZIR, value: PronounOption.ZE_ZIR },
    { name: PronounOption.ZE_HIR, value: PronounOption.ZE_HIR },
    { name: PronounOption.ASK, value: PronounOption.ASK },
  ];
  showFirstNameWarning: boolean = false;

  readonly isSaasEnabledProvider = signal<boolean>(false);

  constructor(
    private appService: SondermindApplicationService,
    private userProfileHttpService: UserProfileHttpService,
    private userService: ProviderUserService,
    public snackBar: MatSnackBar,
    private irisModalService: IrisModalService,
    public maskingService: FieldMaskingService
  ) { }

  ngOnInit(): void {
    this.appService.currentUser$.pipe(
      filter((u) => !!u),
      takeUntil(this.destroyed$)
    ).subscribe((u) => {
      this.user = u;
      this.isSaasEnabledProvider.set(this.user.isSaasEnabled());
      this.userInitial = `${this.user.firstName.charAt(0)}${this.user.lastName.charAt(0)}`;
      if (!this.form) {
        this.initializeForm();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  // @HostListener allows us to also guard against browser refresh, close, etc.
  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return this.form?.pristine;
  }

  initializeForm(): void {
    this.form = new FormGroup<IAboutYouForm>({
      id: new FormControl(this.user?.id, [Validators.required]),
      preferredFirstName: new FormControl(this.user?.preferredFirstName, this.disallowedWordValidator()),
      publicPhoneNumber: new FormControl(this.user?.publicPhoneNumber, [Validators.required, PhoneValidator.format]),
      pronouns: new FormControl(this.user?.pronouns ?? PronounOption.NO_PRONOUNS_SHARED, [Validators.required])
    });
  }

  disallowedWordValidator(): ValidatorFn {
    return (control: FormControl<string>): ValidationErrors | null => {
      this.showFirstNameWarning = false;

      if (!control.value) return null;
      const word = control?.value?.toLowerCase();
      if (!word) return null;
      const hasProfanity = word.split(' ').some((item) => PROFANITY.includes(item));
      const hasDisallowedWord = word.split(' ').some((item) => DISALLOWED_TERMS.includes(item));
      const isMultipleWords = word.split(' ').length > 1;
      if (hasProfanity) {
        return { profanity: null };
      }
      if (hasDisallowedWord) {
        return { validateLanguage: null };
      }
      if (isMultipleWords && !this.form?.pristine && !hasDisallowedWord && !hasProfanity) {
        this.showFirstNameWarning = true;
        return null;
      }
      return null;
    };
  }

  thumbClicked(view: ProfilePicView = ProfilePicView.WEB): void {
    const options: IAPIFetchRequestOptions = { error: {}, view: 'edit' };

    this.userProfileHttpService.getImage(this.user.id, options).pipe(
      filter((userImageData) => !!userImageData),
      switchMap((userImageData) => {
        const modalRef = this.irisModalService.open<
        ProfilePhotoUploaderComponent,
        IProfilePhotoUploaderInput,
        IProfilePhotoUploaderResult
        >(ProfilePhotoUploaderComponent, {
          size: view === ProfilePicView.MOBILE ? IrisModalSize.SM : IrisModalSize.MD,
          title: 'Add your profile picture',
          isDismissable: true,
          data: {
            userId: this.user.id,
            existingImageUrl: userImageData.data.imageUrl,
            cropData: {
              x: userImageData.data.cropX || 0,
              y: userImageData.data.cropY || 0,
              width: userImageData.data.cropW || 0,
              height: userImageData.data.cropH || 0
            }
          }
        });
        return modalRef.afterClosed$();
      }),
      filter((result): result is IProfilePhotoUploaderResult => !!result),
      takeUntil(this.destroyed$)
    ).subscribe(() => {
      this.appService.reloadCurrentUser();
    });
  }

  onCancel(): void {
    if (this.form.pristine) return;

    this.form.patchValue({
      preferredFirstName: this.user?.preferredFirstName,
      publicPhoneNumber: this.user?.publicPhoneNumber,
      pronouns: this.user?.pronouns ?? PronounOption.NO_PRONOUNS_SHARED
    });
    this.form.markAsPristine();
  }

  saveForm(): void {
    if (this.saving || !this.form.valid) {
      this.snackBar.open(
        'Error saving. Please check required fields.',
        'Dismiss',
        { duration: 30000, panelClass: 'toast-color' }
      );
      return;
    }

    this.saving = true;

    const value = <PartialProviderWithId> this.form.value;
    value.pronouns = value.pronouns !== PronounOption.NO_PRONOUNS_SHARED ? value.pronouns : null;
    this.userService.update(value).subscribe({
      next: () => {
        this.appService.reloadCurrentUser();
        this.snackBar.open('Info saved', 'Dismiss');
        this.saving = false;
        this.form.markAsPristine();
      },
      error: () => {
        this.snackBar.open('Info save failed, please try again', 'Dismiss');
        this.saving = false;
      }
    });
  }
}
